import { pathNamesOf, ROOT_ID, typeOf, type TreeIndex } from './node-tree-path.js';

/**
 * 按名字或编码找节点。
 *
 * 排序规则单独放在这儿而不是写在组件里：「哪一条该排前面」是一条会被反复调的规则
 * （敲一个字就重排一次），而它对不对只能靠例子说清。组件负责画，这儿负责判。
 */

export type Hit = {
  /** ROOT_ID 表示企业本身——它不是一条节点记录。 */
  id: string;
  name: string;
  /** 类型名。企业是「企业」，类型查不到的坏节点为 null。 */
  typeName: string | null;
  /** 从企业到它的路径，不含它自己。空数组表示它直接挂在企业下（或它就是企业）。 */
  path: string[];
  /** 编码。组织单元可能没有。 */
  code: string | null;
};

/** 命中的强弱。数越小越靠前。 */
const RANK = {
  codeExact: 0,
  namePrefix: 1,
  nameSubstring: 2,
  codeSubstring: 3,
} as const;

function rankOf(name: string, code: string | null, needle: string): number | null {
  const lowerName = name.toLowerCase();
  const lowerCode = code === null ? null : code.toLowerCase();

  if (lowerCode === needle) return RANK.codeExact;
  if (lowerName.startsWith(needle)) return RANK.namePrefix;
  if (lowerName.includes(needle)) return RANK.nameSubstring;
  if (lowerCode !== null && lowerCode.includes(needle)) return RANK.codeSubstring;
  return null;
}

/** 一次最多给这么多条。再多也不会有人往下翻，而每多一条都要算一次路径。 */
export const SEARCH_LIMIT = 30;

export type SearchResult = {
  hits: Hit[];
  /** 命中总数。大于 hits.length 时说明截断了——截断必须说出来，不能假装就这么多。 */
  total: number;
};

/**
 * 找。空查询返回空——不给「最近看过」之类的兜底列表，那是另一件事（要存状态）。
 *
 * 名字和编码都匹配：编码是印在二维码标签上的东西，从车间回来的人手里拿着的就是它。
 *
 * **归档的资产不在结果里。** 归档就是从日常视野里退下去，搜索是日常视野的一部分。
 * 归档的 ATC 只在设置的归档管理页出现，那是唯一一处。曾经写成「列出来但标着已归档」，
 * 那是错的：它会让一份已经作废的标准出现在人找标准的路上。
 */
export function searchNodes(
  index: TreeIndex,
  rootName: string,
  query: string,
  organizationCode: string | null,
): SearchResult {
  const needle = query.trim().toLowerCase();
  if (needle === '') return { hits: [], total: 0 };

  const scored: { hit: Hit; rank: number }[] = [];

  // 企业本身也能被搜到：它是树上一个可选中的节点，路径的第一段就是它。
  const rootRank = rankOf(rootName, organizationCode, needle);
  if (rootRank !== null) {
    scored.push({
      hit: { id: ROOT_ID, name: rootName, typeName: '企业', path: [], code: organizationCode },
      rank: rootRank,
    });
  }

  for (const node of index.byId.values()) {
    const type = typeOf(index, node);
    // 归档的资产整条跳过。组织节点的 enabled=false 是停用不是归档，照常出现。
    if (type?.kind === 'ASSET' && !node.enabled) continue;

    const rank = rankOf(node.name, node.code, needle);
    if (rank === null) continue;

    scored.push({
      hit: {
        id: node.id,
        name: node.name,
        typeName: type?.name ?? null,
        // 去掉头尾：头是企业（每一条都一样，说了等于没说），尾是它自己（名字已经在旁边）
        path: pathNamesOf(index, node.id, rootName).slice(1, -1),
        code: node.code,
      },
      rank,
    });
  }

  scored.sort(
    (a, b) =>
      a.rank - b.rank ||
      // 名字短的往前：搜「保养」时「保养」比「离心泵季度保养标准」更可能是目标
      a.hit.name.length - b.hit.name.length ||
      a.hit.name.localeCompare(b.hit.name, 'zh'),
  );

  return { hits: scored.slice(0, SEARCH_LIMIT).map((row) => row.hit), total: scored.length };
}
